import * as amplitude from "@amplitude/analytics-browser";
import { Currency } from "@redotech/money/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  getCalculatedRefundValue,
  refundOrder,
} from "@redotech/redo-merchant-app-common/client/shopify";
import { Order } from "@redotech/redo-model/order";
import { Button, ButtonTheme } from "@redotech/redo-web/button";
import { Checkbox } from "@redotech/redo-web/checkbox";
import { Divider } from "@redotech/redo-web/divider";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Modal, ModalSize, PaddingAmount } from "@redotech/redo-web/modal";
import { OrderLineItem } from "@redotech/redo-web/order-line-item";
import { SelectDropdown } from "@redotech/redo-web/select-dropdown";
import { ShippingContactInfo } from "@redotech/redo-web/shipping-contact-info";
import { TextInput } from "@redotech/redo-web/text-input";
import { memo, useEffect, useState } from "react";
import * as refundOrderModalCss from "./refund-order-modal.module.css";

export const RefundOrderModal = memo(function RefundOrderModal({
  open,
  setOpen,
  order,
  onOrdersChange,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  order: Order;
  onOrdersChange: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const refundCalculationOptions = ["Itemized", "Amount", "Percentage"];
  const [refundCalculationType, setRefundCalculationType] = useState(
    refundCalculationOptions[0],
  );
  const [refundAmount, setRefundAmount] = useState("0");
  const [refundPercentage, setRefundPercentage] = useState("0");
  const client = useRequiredContext(RedoMerchantClientContext);
  const lineItems = order.shopify?.line_items;
  const [selectedLineItems, setSelectedLineItems] = useState<Set<string>>(
    new Set(),
  );
  const [includeShipping, setIncludeShipping] = useState(false);
  const [refundAmountLoad, doRefundAmountLoad] = useTriggerLoad(
    async (signal) => {
      const response = await getCalculatedRefundValue(client, {
        shopifyOrderId: order.shopify.id,
        currency:
          order.shopify.total_price_set?.presentment_money.currency_code,
        signal,
      });
      return {
        totalAmount: response.refundAmount,
        lineItemRefundAmounts: response.lineItemRefundAmounts,
        shippingRefundAmount: response.shippingRefundAmount || 0,
      };
    },
  );

  useEffect(() => {
    if (order && lineItems) {
      doRefundAmountLoad();
      setSelectedLineItems(new Set(lineItems.map((item) => String(item.id))));
      setIncludeShipping(true);
    }
  }, [order, lineItems, doRefundAmountLoad]);

  const handleClose = () => {
    setConfirmModalOpen(false);
    setOpen(false);
    setPending(false);
  };
  const handleRefund = async () => {
    setPending(true);
    let amount: number;
    if (refundCalculationType === "Itemized") {
      amount = getItemizedRefundTotal();
    } else if (refundCalculationType === "Amount") {
      amount = Number(refundAmount);
    } else {
      amount =
        (Number(refundPercentage) / 100) *
        (refundAmountLoad.value?.totalAmount ?? 0);
    }
    await refundOrder(client, {
      orderId: order._id,
      notify: notifyCustomer,
      amount,
    });
    amplitude.logEvent("refund-order", { shopifyOrderId: order.shopify_id });
    handleClose();
    onOrdersChange();
  };
  const handleFirstConfirmClick = () => {
    setConfirmModalOpen(true);
  };

  const getAmountRefundable = (lineItemId: number | string) => {
    if (!refundAmountLoad.value) {
      return null;
    }
    const lineItemRefundAmount =
      refundAmountLoad.value.lineItemRefundAmounts.find(
        (lineItemRefundAmount: { id: string; refundAmount: string }) =>
          lineItemRefundAmount.id === String(lineItemId),
      );
    return lineItemRefundAmount?.refundAmount ?? 0;
  };

  const getItemizedRefundTotal = () => {
    if (!refundAmountLoad.value) {
      return 0;
    }
    let total = 0;
    for (const lineItemId of selectedLineItems) {
      const amount = getAmountRefundable(lineItemId);
      if (amount) {
        total += Number(amount);
      }
    }
    if (includeShipping) {
      total += refundAmountLoad.value.shippingRefundAmount;
    }
    return total;
  };

  const toggleLineItem = (lineItemId: string) => {
    const newSelected = new Set(selectedLineItems);
    if (newSelected.has(lineItemId)) {
      newSelected.delete(lineItemId);
    } else {
      newSelected.add(lineItemId);
    }
    setSelectedLineItems(newSelected);
  };

  const isDisabled = () => {
    if (refundCalculationType === "Itemized") {
      return selectedLineItems.size === 0 && !includeShipping;
    }
    if (refundCalculationType === "Amount") {
      return (
        Number(refundAmount) < 0 ||
        Number(refundAmount) > (refundAmountLoad.value?.totalAmount ?? 0)
      );
    }
    if (refundCalculationType === "Percentage") {
      return Number(refundPercentage) < 0 || Number(refundPercentage) > 100;
    }
    return false;
  };

  const Footer = memo(function Footer() {
    return (
      <div className={refundOrderModalCss.actionButtonsContainer}>
        <Button onClick={handleClose} theme={ButtonTheme.OUTLINED}>
          No, don't refund order
        </Button>
        {refundAmountLoad.value?.totalAmount &&
          refundAmountLoad.value.totalAmount > 0 && (
            <Button
              disabled={isDisabled()}
              onClick={() => handleFirstConfirmClick()}
              theme={ButtonTheme.DANGER}
            >
              Yes, refund order
            </Button>
          )}
      </div>
    );
  });
  return (
    <>
      <Modal
        footer={refundAmountLoad.pending ? undefined : <Footer />}
        onClose={handleClose}
        open={open}
        paddingAmount={PaddingAmount.NONE}
        showFooterBorder
        size={ModalSize.MEDIUM}
        title="Refund order"
      >
        {refundAmountLoad.pending ? (
          <div className={refundOrderModalCss.animationContainer}>
            <LoadingRedoAnimation />
          </div>
        ) : (
          <div className={refundOrderModalCss.modalContent}>
            <div className={refundOrderModalCss.orderName}>
              Order {order.shopify.name}
            </div>
            {refundCalculationType === "Itemized" ? (
              <div className={refundOrderModalCss.itemizedSection}>
                <div className={refundOrderModalCss.itemizedHeader}>
                  Select items to refund:
                </div>
                {lineItems.map((lineItem, index) => {
                  const lineItemId = String(lineItem.id);
                  const itemPrice = getAmountRefundable(lineItem.id);
                  return (
                    <div
                      className={refundOrderModalCss.itemizedRow}
                      key={index}
                    >
                      <Checkbox
                        onChange={() => toggleLineItem(lineItemId)}
                        value={selectedLineItems.has(lineItemId)}
                      >
                        <div className={refundOrderModalCss.itemizedContent}>
                          <OrderLineItem index={index} lineItem={lineItem} />
                        </div>
                      </Checkbox>
                      <div className={refundOrderModalCss.itemPrice}>
                        {itemPrice
                          ? `${
                              order.shopify.total_price_set?.presentment_money
                                .currency_code === Currency.USD
                                ? "$"
                                : ""
                            }${Number(itemPrice).toFixed(2)}`
                          : "—"}
                      </div>
                    </div>
                  );
                })}
                {refundAmountLoad.value?.shippingRefundAmount > 0 && (
                  <>
                    <Divider />
                    <div className={refundOrderModalCss.itemizedRow}>
                      <Checkbox
                        onChange={setIncludeShipping}
                        value={includeShipping}
                      >
                        <div className={refundOrderModalCss.shippingLabel}>
                          Shipping
                        </div>
                      </Checkbox>
                      <div className={refundOrderModalCss.itemPrice}>
                        {order.shopify.total_price_set?.presentment_money
                          .currency_code === Currency.USD
                          ? "$"
                          : ""}
                        {refundAmountLoad.value?.shippingRefundAmount.toFixed(
                          2,
                        )}
                      </div>
                    </div>
                  </>
                )}
                <Divider />
                <div className={refundOrderModalCss.totalRow}>
                  <strong>Refund Total:</strong>
                  <strong>
                    {order.shopify.total_price_set?.presentment_money
                      .currency_code === Currency.USD
                      ? "$"
                      : ""}
                    {getItemizedRefundTotal().toFixed(2)}
                  </strong>
                </div>
              </div>
            ) : (
              <>
                {lineItems.map((lineItem, index) => (
                  <OrderLineItem
                    amountRefundable={getAmountRefundable(lineItem.id)}
                    index={index}
                    key={index}
                    lineItem={lineItem}
                  />
                ))}
              </>
            )}
            <ShippingContactInfo order={order} />
            <Divider />
            {refundAmountLoad.value?.totalAmount > 0 && (
              <Checkbox onChange={setNotifyCustomer} value={notifyCustomer}>
                Notify customer
              </Checkbox>
            )}
            <div className={refundOrderModalCss.refundOptionsContainer}>
              <div className={refundOrderModalCss.refundOption}>
                <LabeledInput label="Refund type">
                  <SelectDropdown
                    options={refundCalculationOptions}
                    value={refundCalculationType}
                    valueChange={setRefundCalculationType}
                  >
                    {(option) => (
                      <div>
                        <p>{option.toString()}</p>
                      </div>
                    )}
                  </SelectDropdown>
                </LabeledInput>
              </div>
              {refundCalculationType === "Amount" && (
                <div className={refundOrderModalCss.refundOption}>
                  <LabeledInput
                    description={
                      refundAmountLoad.value?.totalAmount > 0
                        ? `Total available to refund: ${
                            order.shopify.total_price_set?.presentment_money
                              .currency_code === Currency.USD
                              ? "$"
                              : ""
                          }${refundAmountLoad.value?.totalAmount.toFixed(2)} ${
                            order.shopify.total_price_set?.presentment_money
                              .currency_code
                          }`
                        : ""
                    }
                    label="Refund amount"
                  >
                    <TextInput
                      error={
                        Number(refundAmount) >
                          refundAmountLoad.value?.totalAmount ||
                        Number(refundAmount) < 0
                      }
                      max={refundAmountLoad.value?.totalAmount}
                      min={0}
                      onChange={setRefundAmount}
                      prefix="$"
                      type="number"
                      value={refundAmount}
                    />
                  </LabeledInput>
                </div>
              )}
              {refundCalculationType === "Percentage" && (
                <div className={refundOrderModalCss.refundOption}>
                  <LabeledInput
                    description={
                      refundAmountLoad.value?.totalAmount > 0
                        ? `Amount to refund: ${
                            order.shopify.total_price_set?.presentment_money
                              .currency_code === Currency.USD
                              ? "$"
                              : ""
                          }${(
                            refundAmountLoad.value?.totalAmount *
                            (Number(refundPercentage) / 100)
                          ).toFixed(2)} ${
                            order.shopify.total_price_set?.presentment_money
                              .currency_code
                          }`
                        : ""
                    }
                    label="Refund percent"
                  >
                    <TextInput
                      error={
                        Number(refundPercentage) > 100 ||
                        Number(refundPercentage) < 0
                      }
                      max={100}
                      min={0}
                      onChange={setRefundPercentage}
                      step={1}
                      suffix="%"
                      type="number"
                      value={refundPercentage}
                    />
                  </LabeledInput>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
      <Modal
        onClose={() => setConfirmModalOpen(false)}
        open={confirmModalOpen}
        size={ModalSize.SMALL}
        title="Are you sure you want to refund this order?"
      >
        <>
          <Button
            onClick={handleRefund}
            pending={pending}
            theme={ButtonTheme.DANGER}
          >
            Yes, refund order
          </Button>
          <Button onClick={handleClose} theme={ButtonTheme.OUTLINED}>
            No, don't refund order
          </Button>
        </>
      </Modal>
    </>
  );
});
